import { useEffect } from 'react';
import { CurrencySettings } from '@wix/premium-react-text';
import { CheckoutProps, SummaryProps } from '@wix/checkout-components';

import {
  useBeforeInit,
  UseBeforeInitOptions,
  useOnBeforeSubmit,
  UseOnBeforeSubmitOptions,
  useOnInitError,
  UseOnInitErrorOptions,
  useOnInitSuccess,
  UseOnInitSuccessOptions,
  useOnSubmitError,
  UseOnSubmitErrorOptions,
  useOnSubmitPending,
  UseOnSubmitPendingOptions,
  useOnSubmitSuccess,
  UseOnSubmitSuccessOptions,
  useOrderPageCreation,
  UseOrderPageCreationOptions,
  useSelectedPaymentMethod,
  UseSummaryPropsOptions,
  useSummaryProps,
} from './hooks';

interface UserDetails {
  isAnonymous: boolean;
  userName: string;
  userGuid: string;
}

interface BaseCheckoutOptions {
  appName: string;
  locale: string;
  userDetails: UserDetails;
  siteGuid: string;
  currencyCode: string;
  currencySettings: CurrencySettings;
  onPageLoaded?: () => void;
}

export interface UseCheckoutPropsOptions {
  checkoutOptions: BaseCheckoutOptions;
  beforeInitOptions?: UseBeforeInitOptions;
  onBeforeSubmitOptions?: UseOnBeforeSubmitOptions;
  onInitErrorOptions?: UseOnInitErrorOptions;
  onInitSuccessOptions?: UseOnInitSuccessOptions;
  onSubmitErrorOptions?: UseOnSubmitErrorOptions;
  onSubmitPendingOptions?: UseOnSubmitPendingOptions;
  onSubmitSuccessOptions?: UseOnSubmitSuccessOptions;
  orderPageCreationOptions: UseOrderPageCreationOptions;
  summaryPropsOptions: UseSummaryPropsOptions;
}

function buildReporterData(userDetails?: UserDetails, siteGuid?: string) {
  return {
    user: userDetails?.isAnonymous
      ? undefined
      : {
          id: userDetails?.userGuid,
          username: userDetails?.userName,
        },
    siteGuid,
  };
}

function buildCurrencySettings(
  currencySettings?: CurrencySettings,
  currencyCode?: string,
) {
  if (currencySettings && currencyCode) {
    return {
      [currencyCode]: currencySettings,
    };
  }
  return undefined;
}

export function useCheckoutProps(
  options: UseCheckoutPropsOptions,
): CheckoutProps & { summaryProps: SummaryProps } {
  const {
    checkoutOptions,
    beforeInitOptions,
    onInitErrorOptions,
    orderPageCreationOptions,
    onInitSuccessOptions,
    onSubmitErrorOptions,
    onSubmitPendingOptions,
    onSubmitSuccessOptions,
    onBeforeSubmitOptions,
    summaryPropsOptions,
  } = options;

  useEffect(() => {
    if (checkoutOptions?.onPageLoaded) {
      checkoutOptions.onPageLoaded();
    }
  }, []);

  const beforeInit = useBeforeInit(beforeInitOptions);
  const onBeforeSubmit = useOnBeforeSubmit(onBeforeSubmitOptions);
  const onInitError = useOnInitError(onInitErrorOptions);
  const onInitSuccess = useOnInitSuccess(onInitSuccessOptions);
  const onSubmitError = useOnSubmitError(onSubmitErrorOptions);
  const onSubmitPending = useOnSubmitPending(onSubmitPendingOptions);
  const onSubmitSuccess = useOnSubmitSuccess(onSubmitSuccessOptions);
  const orderPageCreation = useOrderPageCreation(orderPageCreationOptions);
  const selectedPaymentMethod = useSelectedPaymentMethod();
  const summaryProps = useSummaryProps(summaryPropsOptions);

  return {
    appName: checkoutOptions?.appName,
    locale: checkoutOptions?.locale,
    reporterData: buildReporterData(
      checkoutOptions?.userDetails,
      checkoutOptions?.siteGuid,
    ),
    currencySettings: buildCurrencySettings(
      checkoutOptions?.currencySettings,
      checkoutOptions?.currencyCode,
    ),
    beforeInit,
    onBeforeSubmit,
    onInitError,
    onInitSuccess,
    onSubmitError,
    onSubmitPending,
    onSubmitSuccess,
    orderPageCreation,
    selectedPaymentMethod,
    summaryProps,
  };
}
